import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Autocomplete from '@mui/joy/Autocomplete';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import axios from 'axios';
import _ from 'lodash';
import { apiURL } from '../API/constants';
import { PERMISSIONS } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import CustomerInvoicedPaymentStatusBanner from './CustomerInvoicedPaymentStatusBanner';

export default function CustomerOptionList({
  onChange, // Called when the selected option changes
  required, // Marks the field as required in the label
  defaultValue, // Currently selected option
  disabled = false, // Disables the autocomplete
  label, // Field label override
  errorMessage, // Validation message shown under the field
  placeholder, // Placeholder override (defaults to customer search)
}) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canListCustomers = hasPermission(PERMISSIONS.CUSTOMERS_LIST);
  const canViewPaymentStatus = hasPermission(PERMISSIONS.USERS_INVOICED_SHIPMENT_PAYMENT_STATUS);
  const resolvedLabel = label ?? t('widgets.customer');
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(
    defaultValue == null || defaultValue === '' ? null : defaultValue
  );

  useEffect(() => {
    setSelectedCustomer(defaultValue == null || defaultValue === '' ? null : defaultValue);
  }, [defaultValue]);

  const handleChange = (event, value) => {
    setSelectedCustomer(value);
    if (onChange) onChange(event, value);
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const getUsers = async (inputValue) => {
    if (!canListCustomers) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${apiURL}/api/customers/?deleted=false&page=1&page_size=30&search=${inputValue || ''}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          withCredentials: true,
        }
      );
      // Ensure we always set an array, even if the API returns unexpected data
      const results = response && response.data && response.data.results;
      setOptions(Array.isArray(results) ? results : []);
    } catch (err) {
      console.error('Error fetching users:', err.message);
      // Set empty array on error to prevent filter error
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedGetUsers = useCallback(_.debounce(getUsers, 300), []);

  useEffect(() => {
    if (inputValue) {
      debouncedGetUsers(inputValue);
    } else {
      setOptions([]);
    }

  }, [inputValue, debouncedGetUsers]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }

  }, [open]);

  return (
    <Box
      sx={{
        marginBottom: 2,
        position: 'relative',
        '&:focus-within': {
          boxShadow: '0 0 8px rgba(0, 128, 255, 0.3)',
          borderRadius: '8px',
        },
      }}
    >
      <FormLabel
        sx={{
          fontWeight: 600,
          color: '#555',
          marginBottom: '6px',
          fontSize: '14px',
        }}
      >
        {required ? `${resolvedLabel} *` : resolvedLabel}
      </FormLabel>
      <Autocomplete
        onInputChange={handleInputChange}
        value={defaultValue == null || defaultValue === '' ? null : defaultValue}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        placeholder={placeholder ?? t('widgets.searchCustomers')}
        options={Array.isArray(options) ? options : []}
        loading={loading}
        disabled={disabled || !canListCustomers}
        onChange={handleChange}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        getOptionLabel={(option) =>
          option.first_name
            ? `${option.first_name} ${option.last_name || ''} - ${option.phone_number}`
            : option.username || ''
        }
        noOptionsText={
          !canListCustomers ? (
            <span>
              {t('permissions.noPermission', {
                defaultValue: 'You do not have permission to perform this action.',
              })}
            </span>
          ) : (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            px={2}
          >
            <span>{t('widgets.typeSomething')}</span>
            <Button
              variant="solid"
              color="primary"
              size="sm"
              onClick={() => window.open('/Customers/AddCustomer', '_blank')}
            >
              {t('widgets.addCustomer')}
            </Button>
          </Box>
          )
        }
        loadingText={t('actions.loading')}
        filterOptions={(options) => options} // Disable default filtering
        endDecorator={
          loading && (
            <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
          )
        }
        sx={{
          backgroundColor: '#f7f9fc',
          borderRadius: '8px',
          padding: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            borderColor: '#5f09c2',
          },
          '&:focus': {
            borderColor: '#5f09c2',
            outline: 'none',
            boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)',
          },
          '&[disabled]': {
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            cursor: 'not-allowed',
          },
          ...(errorMessage
            ? {
                borderColor: 'var(--joy-palette-danger-500, #c41c1c)',
                boxShadow: '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)',
              }
            : {}),
        }}
      />
      {errorMessage ? (
        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{errorMessage}</FormHelperText>
      ) : null}
      <CustomerInvoicedPaymentStatusBanner
        userId={selectedCustomer?.id}
        enabled={canViewPaymentStatus}
      />
    </Box>
  );
}
